import React from 'react';

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react';

import Button from 'components/shared/Button';

import s from './BaseItemButton.module.css';
import * as t from './BaseItemButton.types';

export const BaseItemButton = (props: t.Props) => {
  const { children, onMouseDown, ...rest } = props;
  return (
    <Button
      type="empty"
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onMouseDown && onMouseDown(e);
      }}
      {...rest}
      htmlType="button"
      className={s.btn}
    >
      {children}
    </Button>
  );
};

const withPortal = (WrappedComponent: React.ComponentType<t.Props>) => {
  return React.forwardRef<t.PortalControl, t.Props & t.WithPortalProps>(
    ({ portalRef, portalContent, floatingOptions, onBlur, ...props }, ref) => {
      const [isOpen, setIsOpen] = React.useState(false);
      const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (state) => {
          if (!state) {
            close();
            onBlur?.();
          }
        },
        middleware: [offset(8), flip(), shift()],
        whileElementsMounted: autoUpdate,
        ...floatingOptions,
      });

      const mergedRef = useMergeRefs([refs.setFloating, portalRef]);
      useDismiss(context, { enabled: isOpen });

      const getIsOpen = () => isOpen;
      const open = () => setIsOpen(true);
      const close = () => setIsOpen(false);

      const openWithRect = (rect: DOMRect) => {
        refs.setReference({
          getBoundingClientRect: () => rect,
        });
        setIsOpen(true);
      };

      React.useImperativeHandle(ref, () => ({
        getIsOpen,
        open,
        close,
        openWithRect,
      }));

      return (
        <>
          <WrappedComponent {...props} anchorRef={refs.setReference} />
          {isOpen && (
            <FloatingPortal root={document.body}>
              <div
                ref={mergedRef}
                style={floatingStyles}
                onClick={(e) => e.stopPropagation()}
                className={s.portalWrapper}
              >
                {portalContent}
              </div>
            </FloatingPortal>
          )}
        </>
      );
    },
  );
};

export const BaseItemButtonWithPortal = withPortal(BaseItemButton);
